export const BOOKING_STATUS_OPTIONS = [
  "Confirmed", "RAC", "Waitlisted", "Cancelled", "Pending", "Partially Confirmed"
];

export const PAYMENT_STATUS_OPTIONS = [
  "Paid", "Pending", "Refund Pending", "Refunded", "Not Applicable"
];

export const PASSENGER_STATUS_OPTIONS = [
  "Confirmed", "RAC", "Waitlisted", "Cancelled"
];

export const BERTH_TYPE_OPTIONS = [
  "Lower", "Middle", "Upper", "Side Lower", "Side Upper", "Chair Car", "Not Allocated"
];

export const GENDER_OPTIONS = ["Male", "Female", "Other"];

export const PASSENGER_TYPE_OPTIONS = ["Adult", "Child", "Senior Citizen", "Infant"];

export const emptyPassenger = () => ({
  passengerName: "",
  age: "",
  gender: "",
  berthPreference: "",
  coach: "",
  seatNumber: "",
  berthType: "",
  ticketStatus: "Confirmed",
  passengerType: "Adult",
  idType: "",
  idNumber: "",
  mealPreference: "",
  remarks: "",
});

export const emptyTicket = () => ({
  id: null,
  bookingId: "",
  pnr: "",
  ticketNumber: "",
  bookingDate: "",
  bookingStatus: "Confirmed",
  paymentStatus: "Paid",
  issuedBy: "IRCTC",
  agentOrganizationName: "ZuppTravel",
  specialReferenceNumber: "",
  trainNumber: "",
  trainName: "",
  trainType: "",
  journeyDate: "",
  boardingStation: "",
  boardingStationCode: "",
  destinationStation: "",
  destinationStationCode: "",
  departureDate: "",
  departureTime: "",
  arrivalDate: "",
  arrivalTime: "",
  journeyDuration: "",
  classType: "",
  quota: "",
  boardingPoint: "",
  platformNumber: "",
  coach: "",
  baseFare: 0,
  reservationCharge: 0,
  superfastCharge: 0,
  cateringCharge: 0,
  gst: 0,
  convenienceFee: 0,
  serviceFee: 0,
  insurance: 0,
  otherCharges: 0,
  discount: 0,
  couponDiscount: 0,
  walletAmount: 0,
  totalAmount: 0,
  purchaseAmount: "",
  manualFinalAmount: "",
  manualOverride: false,
  qrCodeData: "",
  barcodeData: "",
  boardingInstructions: "",
  cancellationPolicy: "",
  importantInformation: "",
  termsConditions: "",
  customerRemarks: "",
  internalRemarks: "",
  customerName: "",
  customerMobile: "",
  customerEmail: "",
  customerAddress: "",
  customerCity: "",
  customerState: "",
  customerCountry: "India",
  passengers: [emptyPassenger()],
});

/** HTML date inputs require YYYY-MM-DD; AI often returns "20-Aug-2026" etc. */
export const parseDateForInput = (value) => {
  if (value == null || value === "") return "";
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  let m = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (m) {
    return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  }

  m = s.match(/^(\d{1,2})[-/]([A-Za-z]{3})[-/](\d{4})$/i);
  if (m) {
    const parsed = new Date(`${m[2]} ${m[1]}, ${m[3]}`);
    if (!isNaN(parsed.getTime())) {
      const y = parsed.getFullYear();
      const mo = String(parsed.getMonth() + 1).padStart(2, "0");
      const d = String(parsed.getDate()).padStart(2, "0");
      return `${y}-${mo}-${d}`;
    }
  }

  const parsed = new Date(s);
  if (!isNaN(parsed.getTime())) {
    const y = parsed.getFullYear();
    const mo = String(parsed.getMonth() + 1).padStart(2, "0");
    const d = String(parsed.getDate()).padStart(2, "0");
    return `${y}-${mo}-${d}`;
  }
  return "";
};

/** HTML time inputs require HH:MM (24h); AI often returns "06:10 AM" */
export const parseTimeForInput = (value) => {
  if (value == null || value === "") return "";
  const s = String(value).trim();
  if (/^\d{2}:\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?$/i);
  if (m) {
    let h = parseInt(m[1], 10);
    const min = m[2];
    const ampm = m[3]?.toUpperCase();
    if (ampm === "PM" && h < 12) h += 12;
    if (ampm === "AM" && h === 12) h = 0;
    return `${String(h).padStart(2, "0")}:${min}`;
  }
  return "";
};

export const normalizeGender = (value) => {
  if (!value) return "";
  const g = String(value).trim().toLowerCase();
  if (g === "m" || g === "male" || g.startsWith("male")) return "Male";
  if (g === "f" || g === "female" || g.startsWith("female")) return "Female";
  if (g === "other" || g === "o" || g === "transgender") return "Other";
  for (const opt of GENDER_OPTIONS) {
    if (opt.toLowerCase() === g) return opt;
  }
  return "";
};

export const applyCustomerDefaults = (ticket) => {
  const t = { ...ticket };
  const first = t.passengers?.[0];
  if (!first) return t;
  if (!String(t.customerName || "").trim() && String(first.passengerName || "").trim()) {
    t.customerName = first.passengerName.trim();
  }
  return t;
};

export const normalizeTicketForForm = (ticket) => {
  if (!ticket) return emptyTicket();
  let t = applyCustomerDefaults({ ...emptyTicket(), ...ticket });
  if (!String(t.issuedBy || "").trim()) t.issuedBy = "IRCTC";
  if (!String(t.agentOrganizationName || "").trim()) t.agentOrganizationName = "ZuppTravel";
  ["bookingDate", "journeyDate", "departureDate", "arrivalDate"].forEach((f) => {
    if (t[f]) t[f] = parseDateForInput(t[f]);
  });
  if (t.departureTime) t.departureTime = parseTimeForInput(t.departureTime);
  if (t.arrivalTime) t.arrivalTime = parseTimeForInput(t.arrivalTime);
  t.passengers = (t.passengers?.length ? t.passengers : [emptyPassenger()]).map((p) => ({
    ...emptyPassenger(),
    ...p,
    age: p.age != null && p.age !== "" ? String(p.age) : "",
    gender: normalizeGender(p.gender),
  }));
  if (t.pnr) t.barcodeData = t.pnr;
  return t;
};

export const calcFare = (ticket) => {
  const num = (v) => Math.max(0, parseFloat(v) || 0);
  const gross =
    num(ticket.baseFare) +
    num(ticket.reservationCharge) +
    num(ticket.superfastCharge) +
    num(ticket.cateringCharge) +
    num(ticket.gst) +
    num(ticket.convenienceFee) +
    num(ticket.serviceFee) +
    num(ticket.insurance) +
    num(ticket.otherCharges);
  const discounts =
    num(ticket.discount) + num(ticket.couponDiscount) + num(ticket.walletAmount);
  const calculated = Math.max(0, gross - discounts);
  if (ticket.manualOverride && ticket.manualFinalAmount !== "") {
    return num(ticket.manualFinalAmount);
  }
  return calculated;
};

export const mapTicketToForm = (ticket) => normalizeTicketForForm(ticket);

export const mapExtractedToTicket = (data) => {
  if (!data) return emptyTicket();
  const ticket = emptyTicket();
  const fieldMap = {
    bookingId: "bookingId",
    pnr: "pnr",
    ticketNumber: "ticketNumber",
    bookingDate: "bookingDate",
    bookingStatus: "bookingStatus",
    paymentStatus: "paymentStatus",
    issuedBy: "issuedBy",
    agentOrganizationName: "agentOrganizationName",
    specialReferenceNumber: "specialReferenceNumber",
    trainNumber: "trainNumber",
    trainName: "trainName",
    trainType: "trainType",
    journeyDate: "journeyDate",
    boardingStation: "boardingStation",
    boardingStationCode: "boardingStationCode",
    destinationStation: "destinationStation",
    destinationStationCode: "destinationStationCode",
    departureDate: "departureDate",
    departureTime: "departureTime",
    arrivalDate: "arrivalDate",
    arrivalTime: "arrivalTime",
    journeyDuration: "journeyDuration",
    classType: "classType",
    quota: "quota",
    boardingPoint: "boardingPoint",
    platformNumber: "platformNumber",
    coach: "coach",
    baseFare: "baseFare",
    reservationCharge: "reservationCharge",
    superfastCharge: "superfastCharge",
    cateringCharge: "cateringCharge",
    gst: "gst",
    convenienceFee: "convenienceFee",
    serviceFee: "serviceFee",
    insurance: "insurance",
    otherCharges: "otherCharges",
    discount: "discount",
    couponDiscount: "couponDiscount",
    walletAmount: "walletAmount",
    totalAmount: "totalAmount",
    purchaseAmount: "purchaseAmount",
    qrCodeData: "qrCodeData",
    barcodeData: "barcodeData",
    boardingInstructions: "boardingInstructions",
    cancellationPolicy: "cancellationPolicy",
    importantInformation: "importantInformation",
    termsConditions: "termsConditions",
    customerName: "customerName",
    customerMobile: "customerMobile",
    customerEmail: "customerEmail",
    customerAddress: "customerAddress",
    customerCity: "customerCity",
    customerState: "customerState",
    customerCountry: "customerCountry",
  };
  Object.entries(fieldMap).forEach(([from, to]) => {
    if (data[from] !== undefined && data[from] !== null && data[from] !== "") {
      ticket[to] = data[from];
    }
  });
  if (data.passengers && data.passengers.length) {
    ticket.passengers = data.passengers.map((p) => ({ ...emptyPassenger(), ...p }));
  }
  return normalizeTicketForForm(ticket);
};

export const preparePayload = (ticket) => {
  const normalized = applyCustomerDefaults(ticket);
  return {
  ...normalized,
  baseFare: parseFloat(ticket.baseFare) || 0,
  reservationCharge: parseFloat(ticket.reservationCharge) || 0,
  superfastCharge: parseFloat(ticket.superfastCharge) || 0,
  cateringCharge: parseFloat(ticket.cateringCharge) || 0,
  gst: parseFloat(ticket.gst) || 0,
  convenienceFee: parseFloat(ticket.convenienceFee) || 0,
  serviceFee: parseFloat(ticket.serviceFee) || 0,
  insurance: parseFloat(ticket.insurance) || 0,
  otherCharges: parseFloat(ticket.otherCharges) || 0,
  discount: parseFloat(ticket.discount) || 0,
  couponDiscount: parseFloat(ticket.couponDiscount) || 0,
  walletAmount: parseFloat(ticket.walletAmount) || 0,
  totalAmount: calcFare(ticket),
  purchaseAmount: ticket.purchaseAmount !== "" ? parseFloat(ticket.purchaseAmount) || 0 : null,
  manualFinalAmount: ticket.manualOverride ? parseFloat(ticket.manualFinalAmount) || 0 : null,
  barcodeData: normalized.pnr || "",
  passengers: normalized.passengers.map((p, i) => ({
    ...p,
    sortOrder: i,
    age: p.age === "" ? null : parseInt(p.age, 10),
  })),
};
};
